/**
 * The one formatter. Every number, amount, size, duration and date rendered in the UI goes
 * through here (docs/design-system/language/content.md, "Numbers, money, time").
 *
 * - Always `en-US`, whatever the machine is set to: an `en-IN` machine must not print `2.5L`,
 *   `3Cr` or `2,50,000`. Nothing in this file reads the OS locale.
 * - Compact suffixes are uppercase `K`, `M`, `B`, `T`, at most 1 decimal for counts.
 * - Negative numbers use the minus sign U+2212.
 *
 * `metric.ts` re-exports the KPI helpers from here; page code never calls `Intl` or
 * `toLocaleString` itself.
 */

export const LOCALE = 'en-US';
/** U+2212, the typographic minus (content.md, punctuation). */
export const MINUS = '−';

const SEC = 1000;
const MIN = 60 * SEC;
const HOUR = 60 * MIN;
const DAY = 24 * HOUR;

// ---------------------------------------------------------------- numbers

const grouped = new Intl.NumberFormat(LOCALE, { maximumFractionDigits: 0 });
const SUFFIXES: Array<[number, string]> = [
  [1e12, 'T'],
  [1e9, 'B'],
  [1e6, 'M'],
  [1e3, 'K'],
];

function sign(n: number): string {
  return n < 0 ? MINUS : '';
}

function trimZeros(s: string): string {
  return s.includes('.') ? s.replace(/\.?0+$/, '') : s;
}

/** "1,284". Grouped with commas, rounded to a whole number. The exact value for tooltips. */
export function formatInt(n: number): string {
  if (!Number.isFinite(n)) return '0';
  const r = Math.round(n);
  return `${sign(r)}${grouped.format(Math.abs(r))}`;
}

/** Decimals that drop trailing zeros: formatDecimal(4.50, 2) is "4.5". Grouped, en-US. */
export function formatDecimal(n: number, maxDigits = 1): string {
  if (!Number.isFinite(n)) return '0';
  const a = Math.abs(n);
  const s = trimZeros(a.toFixed(maxDigits));
  const [whole = '0', frac] = s.split('.');
  const body = `${grouped.format(Number(whole))}${frac ? `.${frac}` : ''}`;
  return body === '0' ? '0' : `${sign(n)}${body}`;
}

/**
 * Compact with an uppercase suffix: "950", "12.3K", "4.5M", "1.2B". `digits` is the most
 * decimals after the suffix (default 1). A value that rounds up to 1000 of one suffix steps
 * up to the next ("999.96K" is "1M").
 */
export function formatCompactNumber(n: number, digits = 1): string {
  if (!Number.isFinite(n)) return '0';
  const a = Math.abs(n);
  if (a < 1000) return formatDecimal(n, a < 10 && !Number.isInteger(n) ? digits : 0);
  let i = SUFFIXES.findIndex(([size]) => a >= size);
  const at = (k: number) => SUFFIXES[k] ?? [1, ''];
  if (i > 0 && Number((a / at(i)[0]).toFixed(digits)) >= 1000) i -= 1;
  const [size, suffix] = at(i);
  return `${sign(n)}${trimZeros((a / size).toFixed(digits))}${suffix}`;
}

/** A count: grouped under 10,000 ("1,234"), compact from 10,000 ("12.3K"). */
export function formatCount(n: number): string {
  if (!Number.isFinite(n)) return '0';
  return Math.abs(n) < 10_000 ? formatInt(n) : formatCompactNumber(n);
}

/** Kept for existing callers: the same as formatCount. */
export function formatCompact(n: number): string {
  return formatCount(n);
}

/** "64%" from a 0..1 fraction; at most `digits` decimals (default 0, never more than 1 by the rules). */
export function formatPercent(fraction: number, digits = 0): string {
  if (!Number.isFinite(fraction)) return '0%';
  const v = fraction * 100;
  return `${sign(v)}${trimZeros(Math.abs(v).toFixed(digits))}%`;
}

/** "10 MB", "1.2 GB": decimal units (1000), a space before the unit. */
export function formatBytes(bytes: number, digits = 1): string {
  if (!Number.isFinite(bytes) || bytes <= 0) return '0 B';
  const units = ['B', 'KB', 'MB', 'GB', 'TB', 'PB'];
  let i = Math.min(units.length - 1, Math.floor(Math.log10(bytes) / 3));
  if (i > 0 && Number((bytes / 1000 ** i).toFixed(digits)) >= 1000 && i < units.length - 1) i += 1;
  const v = bytes / 1000 ** i;
  return `${i === 0 ? Math.round(v) : trimZeros(v.toFixed(v >= 100 ? 0 : digits))} ${units[i]}`;
}

/** "3 sessions", "1 session", "12.3K sessions". */
export function plural(n: number, one: string, many = `${one}s`): string {
  return `${formatCount(n)} ${n === 1 ? one : many}`;
}

// ---------------------------------------------------------------- money

/**
 * Money for totals and KPI tiles, in US dollars: "$0.42", "$12.40", "$1,284", "$19.1K".
 * Cents under $100, whole dollars to $10,000, compact above. Under a cent: "<$0.01".
 */
export function formatMoney(usd: number, currency = '$'): string {
  if (!Number.isFinite(usd)) return `${currency}0.00`;
  const a = Math.abs(usd);
  if (a > 0 && a < 0.005) return `<${currency}0.01`;
  if (a < 100) return `${sign(usd)}${currency}${a.toFixed(2)}`;
  if (a < 10_000) return `${sign(usd)}${currency}${grouped.format(Math.round(a))}`;
  return `${sign(usd)}${currency}${formatMetric(a)}`;
}

/** Money for per-item columns: always 2 decimals, grouped: "$826.10", "$1,284.20". */
export function formatMoneyExact(usd: number, currency = '$'): string {
  if (!Number.isFinite(usd)) return `${currency}0.00`;
  const a = Math.abs(usd);
  if (a > 0 && a < 0.005) return `<${currency}0.01`;
  const [whole = '0', cents = '00'] = a.toFixed(2).split('.');
  return `${sign(usd)}${currency}${grouped.format(Number(whole))}.${cents}`;
}

/** Money on a chart axis: whole and compact, "$0", "$50", "$1.2K". */
export function formatMoneyCompact(usd: number, currency = '$'): string {
  if (!Number.isFinite(usd)) return `${currency}0`;
  return `${sign(usd)}${currency}${formatCompactNumber(Math.abs(usd))}`;
}

// ---------------------------------------------------------------- KPI values

/** 3 significant digits, so 12.7, 128, 1.42 (never 128.4). */
function sig3(n: number): string {
  const a = Math.abs(n);
  const digits = a >= 100 ? 0 : a >= 10 ? 1 : 2;
  return trimZeros(a.toFixed(digits));
}

/**
 * A KPI value, at most 4 significant characters before abbreviating: "0", "42", "0.42",
 * "1,284", "12.7K", "128K", "1.42M". Under 10,000 the full number shows (up to two
 * decimals below 10); from 10,000 it abbreviates.
 */
export function formatMetric(n: number): string {
  if (!Number.isFinite(n)) return '0';
  const a = Math.abs(n);
  if (a < 10) return `${a === 0 ? '' : sign(n)}${trimZeros(a.toFixed(2))}`;
  if (a < 100) return `${sign(n)}${trimZeros(a.toFixed(1))}`;
  if (a < 10_000) return formatInt(n);
  let i = SUFFIXES.findIndex(([size]) => a >= size);
  if (i < 0) i = SUFFIXES.length - 1;
  const at = (k: number) => SUFFIXES[k] ?? [1, ''];
  // 999.6K rounds to "1000K": step up to the next suffix instead ("1M").
  if (i > 0 && Number(sig3(a / at(i)[0])) >= 1000) i -= 1;
  const [size, suffix] = at(i);
  return `${sign(n)}${sig3(a / size)}${suffix}`;
}

/** "820 tokens", "4.9K tokens", "1.42M tokens", "1 token". */
export function formatTokens(n: number): string {
  const v = Math.max(0, Math.round(Number.isFinite(n) ? n : 0));
  return `${formatCompactNumber(v)} ${v === 1 ? 'token' : 'tokens'}`;
}

export type DeltaDirection = 'up' | 'down' | 'flat';

/** Whether a change is good, bad or neither for this metric (cost up is bad; success rate up is good). */
export type DeltaMeaning = 'good' | 'bad' | 'neutral';

export function deltaDirection(delta: number): DeltaDirection {
  if (!Number.isFinite(delta) || Math.abs(delta) < 1e-9) return 'flat';
  return delta > 0 ? 'up' : 'down';
}

/** Is this change good or bad, given which way is good for the metric? */
export function deltaMeaning(delta: number, goodWhen: 'up' | 'down' | 'neither' = 'up'): DeltaMeaning {
  const dir = deltaDirection(delta);
  if (dir === 'flat' || goodWhen === 'neither') return 'neutral';
  return dir === goodWhen ? 'good' : 'bad';
}

/** "+12%", "−3.4%", "0%" from a fraction; "+1,284" or "−$0.42" for absolute changes. */
export function formatDelta(delta: number, kind: 'percent' | 'absolute' | 'money' = 'percent'): string {
  if (!Number.isFinite(delta)) return '0';
  const s = delta > 0 ? '+' : delta < 0 ? MINUS : '';
  const a = Math.abs(delta);
  if (kind === 'percent') {
    const pct = a * 100;
    return `${s}${pct >= 10 || pct === 0 ? Math.round(pct) : trimZeros(pct.toFixed(1))}%`;
  }
  if (kind === 'money') return `${s}${formatMoney(a)}`;
  return `${s}${formatMetric(a)}`;
}

// ---------------------------------------------------------------- time

/** "420ms", "12s", "3m 12s", "1h 05m", "2d 4h": the two largest units, the second zero-padded. */
export function formatDuration(ms: number): string {
  if (!Number.isFinite(ms) || ms < 0) return '0s';
  if (ms < SEC) return `${Math.round(ms)}ms`;
  if (ms < MIN) return `${Math.floor(ms / SEC)}s`;
  if (ms < HOUR) {
    const m = Math.floor(ms / MIN);
    const s = Math.floor((ms % MIN) / SEC);
    return s ? `${m}m ${String(s).padStart(2, '0')}s` : `${m}m`;
  }
  if (ms < DAY) {
    const h = Math.floor(ms / HOUR);
    const m = Math.floor((ms % HOUR) / MIN);
    return m ? `${h}h ${String(m).padStart(2, '0')}m` : `${h}h`;
  }
  const d = Math.floor(ms / DAY);
  const h = Math.floor((ms % DAY) / HOUR);
  return h ? `${d}d ${h}h` : `${d}d`;
}

/**
 * A running time for a column, to the minute: "45s", "13m", "2h 09m", "3d 4h". Seconds only under a
 * minute, so a live column does not tick on every second.
 */
export function formatElapsed(ms: number): string {
  if (!Number.isFinite(ms) || ms < 0) return '0s';
  if (ms < MIN) return `${Math.floor(ms / SEC)}s`;
  if (ms < HOUR) return `${Math.floor(ms / MIN)}m`;
  return formatDuration(ms - (ms % MIN));
}

const monthDay = new Intl.DateTimeFormat(LOCALE, { month: 'short', day: 'numeric' });
const monthDayYear = new Intl.DateTimeFormat(LOCALE, { year: 'numeric', month: 'short', day: 'numeric' });
const monthLong = new Intl.DateTimeFormat(LOCALE, { month: 'long' });
const clock = new Intl.DateTimeFormat(LOCALE, { hour: 'numeric', minute: '2-digit' });
const absolute = new Intl.DateTimeFormat(LOCALE, {
  weekday: 'short',
  year: 'numeric',
  month: 'short',
  day: 'numeric',
  hour: 'numeric',
  minute: '2-digit',
  second: '2-digit',
  timeZoneName: 'short',
});

function sameDay(a: Date, b: Date): boolean {
  return a.getFullYear() === b.getFullYear() && a.getMonth() === b.getMonth() && a.getDate() === b.getDate();
}

/** "Oct 17" this year, "Oct 17, 2025" otherwise. */
export function formatDate(ts: number, now: number = Date.now()): string {
  const d = new Date(ts);
  return d.getFullYear() === new Date(now).getFullYear() ? monthDay.format(d) : monthDayYear.format(d);
}

/** "Oct 17", or "Oct 17, 2025" with the year: a day on an axis or in a range. */
export function formatDay(ts: number, withYear = false): string {
  return (withYear ? monthDayYear : monthDay).format(new Date(ts));
}

/** "October 2026", a calendar's title. */
export function formatMonthYear(ts: number): string {
  return new Date(ts).toLocaleDateString(LOCALE, { month: 'long', year: 'numeric' });
}

/** "Saturday, October 17, 2026", a calendar day's accessible name. */
export function formatLongDay(ts: number): string {
  return new Date(ts).toLocaleDateString(LOCALE, { weekday: 'long', month: 'long', day: 'numeric', year: 'numeric' });
}

/** "3 PM", an hour on an axis. */
export function formatHour(ts: number): string {
  return new Date(ts).toLocaleTimeString(LOCALE, { hour: 'numeric' });
}

/** "October". */
export function formatMonth(ts: number): string {
  return monthLong.format(new Date(ts));
}

/** "3:04 PM". */
export function formatClock(ts: number): string {
  return clock.format(new Date(ts));
}

/** Full date and time with the zone, for tooltips: "Sat, Oct 17, 2026, 3:04:05 PM GMT+4". */
export function formatAbsolute(ts: number): string {
  return absolute.format(new Date(ts));
}

/**
 * "just now" under a minute, then "5m ago", "3h ago", "yesterday", "4d ago" within a week,
 * then a date. Future times read "in 5m".
 */
export function formatRelative(ts: number, now: number = Date.now()): string {
  const diff = now - ts;
  const abs = Math.abs(diff);
  const future = diff < 0;
  if (abs < MIN) return 'just now';
  let text: string;
  if (abs < HOUR) text = `${Math.floor(abs / MIN)}m`;
  else if (abs < DAY) text = `${Math.floor(abs / HOUR)}h`;
  else {
    const yesterday = new Date(now - DAY);
    if (!future && sameDay(new Date(ts), yesterday)) return 'yesterday';
    if (abs < 7 * DAY) text = `${Math.floor(abs / DAY)}d`;
    else return formatDate(ts, now);
  }
  return future ? `in ${text}` : `${text} ago`;
}

/** How often a relative label for this timestamp needs re-rendering. */
export function relativeRefreshMs(ts: number, now: number = Date.now()): number {
  const abs = Math.abs(now - ts);
  if (abs < HOUR) return 15 * SEC;
  return MIN;
}

// ---------------------------------------------------------------- text

/** Last segment of a path, for compact display. */
export function baseName(p: string): string {
  const parts = p.replace(/\\/g, '/').replace(/\/+$/, '').split('/');
  return parts[parts.length - 1] || p;
}

/** Shorten a path for display: the home directory becomes ~ and the middle collapses. */
export function shortPath(p: string, max = 48): string {
  let s = p.replace(/\\/g, '/').replace(/^[A-Za-z]:\/Users\/[^/]+/, '~').replace(/^\/(Users|home)\/[^/]+/, '~');
  if (s.length <= max) return s;
  const parts = s.split('/');
  // Once the second segment is the ellipsis, each pass removes one more segment (else this never ends).
  while (parts.length > 3 && parts.join('/').length > max - 2) parts.splice(1, parts[1] === '…' ? 2 : 1, '…');
  s = parts.filter((x, i, a) => !(x === '…' && a[i - 1] === '…')).join('/');
  return s;
}

/** Cut a long string in the middle, keeping its start and its more telling end: "abcdef…uvwxyz". */
export function middleEllipsis(s: string, max = 32): string {
  if (s.length <= max) return s;
  const keep = Math.max(2, max - 1);
  const head = Math.ceil(keep * 0.4);
  return `${s.slice(0, head)}…${s.slice(s.length - (keep - head))}`;
}

const PATHISH = /[\\/]/;

/**
 * A sentence whose long tokens are shortened from the middle, for one-line titles and toasts:
 * "Allowed once: Edit /Users/me/code/app/shared-config/tsconfig.base.json" reads
 * "Allowed once: Edit ~/…/shared-config/tsconfig.base.json". Paths keep their last segments,
 * other long words (a URL, a hash) keep both ends. Short words are left alone.
 */
export function shortText(text: string, maxToken = 36): string {
  return text
    .split(/(\s+)/)
    .map((w) => {
      if (w.length <= maxToken || /^\s+$/.test(w)) return w;
      if (PATHISH.test(w) && !/^[a-z]+:\/\//i.test(w)) {
        const p = shortPath(w, maxToken);
        return p.length <= maxToken ? p : middleEllipsis(p, maxToken);
      }
      return middleEllipsis(w, maxToken);
    })
    .join('');
}

/** First 7 characters of a commit sha. */
export function shortSha(sha: string): string {
  return sha.slice(0, 7);
}

/** This browser's IANA time zone ("Europe/London"), or UTC when it does not say. */
export function localTimeZone(): string {
  try {
    return new Intl.DateTimeFormat('en-US').resolvedOptions().timeZone || 'UTC';
  } catch {
    return 'UTC';
  }
}

/**
 * A name as people read it: a repo, project or person named all in lower case gets a capital at
 * the start of each part ("orgus" to "Orgus", "billing-service" to "Billing-Service",
 * "maya chen" to "Maya Chen"). A name with any capital is kept exactly as its owner wrote it
 * ("Qawl-Agents", "iOS-app"). Display only: ids, paths and searches use the real name.
 */
export function formatName(name: string): string {
  if (!name || /\p{Lu}/u.test(name)) return name;
  return name.replace(/(^|[\s\-_./])(\p{Ll})/gu, (_, sep: string, ch: string) => sep + ch.toUpperCase());
}
